import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { env } from '../config/env';
import { useAuthStore } from '../stores/auth';

/**
 * MED-953 / E2.3d — read-only live AI-listening state for a session.
 *
 * Lean Socket.IO subscription to session-intelligence that ONLY tracks the
 * `ai_listening_state` broadcast (coach pause/resume). Used by the participant
 * active session screen, whose other state arrives via the 2s active-state
 * poll — the pause signal lives in session-intelligence memory, so the socket
 * is the source of truth and is real-time per AC4. The server also emits the
 * current state on connect, so late joiners see an in-progress pause.
 *
 * The coach screen does NOT use this hook — its `useSessionNudges` socket
 * carries the same event (one connection per client).
 */
export function useAiListeningLiveState(input: {
  sessionId: string | undefined;
  enabled: boolean;
}): { paused: boolean } {
  const { sessionId, enabled } = input;
  const accessToken = useAuthStore(state => state.accessToken);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!enabled || !sessionId) {
      setPaused(false);
      return;
    }
    const socket = io(env.sessionIntelligenceUrl, {
      query: { sessionId },
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
    });
    socket.on('ai_listening_state', (data: { paused: boolean }) => setPaused(data.paused));
    // A dropped connection means we can no longer claim the AI is paused —
    // fall back to the default listening pill rather than a stale paused one.
    socket.on('disconnect', () => setPaused(false));
    return () => {
      socket.disconnect();
      setPaused(false);
    };
  }, [enabled, sessionId, accessToken]);

  return { paused };
}
